// FakePlatformClient — dice's DEV/TEST stand-in for rgs-core-api-gw + rgs-runtime (relocated out of the deleted
// src/sdk mirror in sc-55115). It is dev scaffolding, NOT shipped SDK: the shared package keeps its own fake
// unshipped under src/testing/ for the same reason, so each consumer owns its dev fake. It reproduces
// bet→resolve with idempotency (same clientRoundId ⇒ 200-replay), real provably-fair (RT-4, dice's local pf),
// and money in minor units, persisting to localStorage so replay + recovery survive a reload. The REAL path
// swaps this for the package's DPoP-bound HTTP adapter (createGameSDK({ rgsApiMode:"real" })).

import {
  ERROR_CODE,
  type BalanceSnapshot,
  type Fairness,
  type LaunchParams,
  type PlatformClient,
  type PlayInput,
  type RoundResult,
  type SessionInfo,
} from "@blocklabsdev/rgs-game-manager-frontend-api";
import { commitment, deriveRandoms, fromHex, randomBytes, toHex } from "@blocklabsdev/rgs-game-manager-frontend-api";
import type { GameResolver } from "../dice-resolver";

export interface FakeOptions {
  currency?: string;
  balanceMinor?: number;
  minBetMinor?: number;
  maxBetMinor?: number;
  storageKey?: string;
}

interface StoredRound {
  input: PlayInput;
  result: RoundResult;
}

interface Snapshot {
  balanceMinor: number;
  currency: string;
  clientSeed: string;
  nonce: number;
  serverSeedHex: string;
  commitmentId: string;
  rounds: [string, StoredRound][];
}

const DEFAULT_KEY = "blocklabs.dice.fake";

export class FakePlatformClient implements PlatformClient {
  private balanceMinor: number;
  private readonly currency: string;
  private readonly minBet: number;
  private readonly maxBet: number;
  private readonly storageKey: string;

  private clientSeed: string;
  private nonce: number;
  private serverSeed: Uint8Array;
  private commitmentId: string;
  private readonly rounds = new Map<string, StoredRound>();

  constructor(
    private readonly resolver: GameResolver,
    opts: FakeOptions = {},
  ) {
    this.currency = opts.currency ?? "USD";
    this.minBet = opts.minBetMinor ?? 10;
    this.maxBet = opts.maxBetMinor ?? 1_000_000;
    this.storageKey = opts.storageKey ?? DEFAULT_KEY;

    const snap = this.load();
    if (snap) {
      this.balanceMinor = snap.balanceMinor;
      this.clientSeed = snap.clientSeed;
      this.nonce = snap.nonce;
      this.serverSeed = fromHex(snap.serverSeedHex);
      this.commitmentId = snap.commitmentId;
      for (const [id, r] of snap.rounds) this.rounds.set(id, r);
    } else {
      this.balanceMinor = opts.balanceMinor ?? 1_000_000;
      this.clientSeed = toHex(randomBytes(8));
      this.nonce = 0;
      this.serverSeed = randomBytes(32);
      this.commitmentId = crypto.randomUUID();
      this.save();
    }
  }

  async openSession(_params: LaunchParams): Promise<SessionInfo> {
    return { session: `demo-${crypto.randomUUID()}`, balanceMinor: this.balanceMinor, currency: this.currency, mode: "demo" };
  }

  async readBalance(_session: string): Promise<BalanceSnapshot> {
    return { balanceMinor: this.balanceMinor };
  }

  async resolveRound(input: PlayInput): Promise<RoundResult> {
    // Idempotency: same clientRoundId ⇒ replay (200); different selection/bet ⇒ ROUND_CONFLICT (§4).
    const prev = this.rounds.get(input.clientRoundId);
    if (prev) {
      const same =
        prev.input.bet.amountMinor === input.bet.amountMinor &&
        JSON.stringify(prev.input.selection) === JSON.stringify(input.selection);
      return same ? prev.result : { status: "denied", code: ERROR_CODE.ROUND_CONFLICT, roundId: input.clientRoundId };
    }

    const bet = input.bet.amountMinor;
    if (!Number.isInteger(bet) || bet < this.minBet || bet > this.maxBet) {
      return { status: "denied", code: ERROR_CODE.ROUND_REJECTED, roundId: input.clientRoundId };
    }
    if (bet > this.balanceMinor) {
      return { status: "denied", code: ERROR_CODE.INSUFFICIENT_FUNDS, roundId: input.clientRoundId };
    }

    // Commit (server-side, before resolving) and reserve the debit.
    const commit = await commitment(this.serverSeed);
    this.balanceMinor -= bet;

    const randoms = await deriveRandoms(
      { serverSeed: this.serverSeed, publicSeed: this.clientSeed, nonce: this.nonce },
      this.resolver.randomsPerRound,
    );
    const out = this.resolver.resolve(randoms, bet, input.selection);

    if (out.result === "void") {
      this.balanceMinor += bet; // rollback
      const voided: RoundResult = { status: "denied", code: ERROR_CODE.ROUND_VOID, roundId: input.clientRoundId };
      this.rotate();
      return voided;
    }

    this.balanceMinor += out.winMinor;
    const fairness: Fairness = {
      commitment: commit,
      commitmentId: this.commitmentId,
      serverSeed: toHex(this.serverSeed),
      clientSeed: this.clientSeed,
      revealedNonce: this.nonce,
    };
    // The shared SDK's SettledRound is money + fairness only (no game detail). dice re-derives the roll +
    // win/lose from `fairness` for the render (outcomeFromFairness) — same seeds ⇒ same outcome.
    const result: RoundResult = {
      status: "settled",
      roundId: input.clientRoundId,
      payoutMinor: out.winMinor,
      balanceMinor: this.balanceMinor,
      fairness,
    };
    this.rounds.set(input.clientRoundId, { input, result });
    this.rotate();
    return result;
  }

  private rotate(): void {
    // FRESH server seed per round (⇒ safe per-round reveal) + nonce++.
    this.nonce += 1;
    this.serverSeed = randomBytes(32);
    this.commitmentId = crypto.randomUUID();
    this.save();
  }

  private load(): Snapshot | undefined {
    try {
      const raw = globalThis.localStorage?.getItem(this.storageKey);
      if (!raw) return undefined;
      return JSON.parse(raw) as Snapshot;
    } catch {
      return undefined;
    }
  }

  private save(): void {
    try {
      const snap: Snapshot = {
        balanceMinor: this.balanceMinor,
        currency: this.currency,
        clientSeed: this.clientSeed,
        nonce: this.nonce,
        serverSeedHex: toHex(this.serverSeed),
        commitmentId: this.commitmentId,
        rounds: [...this.rounds.entries()],
      };
      globalThis.localStorage?.setItem(this.storageKey, JSON.stringify(snap));
    } catch {
      /* storage unavailable: the fake keeps working in memory */
    }
  }
}
